import React, { useState } from 'react';
import styled from 'styled-components';
import { Container, Section, PageHero, HeroArt, Reveal, Marquee, Kicker, hudCompactControl } from '../components/ui';
import { TOPICS } from '../data/conference';

/* atomic-orbit motif — copper electrons circling a nucleus */
const TopicsArt = (
  <HeroArt viewBox="0 0 360 260" aria-hidden="true">
    <g className="spin">
      <ellipse className="frame soft" cx="180" cy="130" rx="150" ry="56" />
      <circle className="node" cx="330" cy="130" r="4" />
    </g>
    <g className="spin" style={{ animationDuration: '52s' }}>
      <ellipse className="frame soft" cx="180" cy="130" rx="150" ry="56" transform="rotate(60 180 130)" />
      <circle className="node" cx="255" cy="0" r="3.4" transform="rotate(60 180 130)" />
    </g>
    <g className="spin-rev">
      <ellipse className="frame soft" cx="180" cy="130" rx="150" ry="56" transform="rotate(120 180 130)" />
      <circle className="node" cx="105" cy="0" r="3.4" transform="rotate(120 180 130)" />
    </g>
    <polygon className="frame" points="180,104 203,117 203,143 180,156 157,143 157,117" />
    <circle className="node" cx="180" cy="130" r="5" />
  </HeroArt>
);

/*
 * The Periodic Table of Tracks — every conference topic rendered as an
 * element tile. Select a tile to load its details into the spec panel.
 */

const ELEMENT_SYMBOLS = ['Sd', 'No', 'Ic', 'Vl', 'Mf', 'Re', 'Wc', 'Sp', 'Ai', 'Me', 'Mw', 'Rb'];

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: clamp(1.5rem, 4vw, 3rem);
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.8rem;

  @media (max-width: 560px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.6rem;
  }
`;

const ElementTile = styled.button`
  --cut: 12px;
  aspect-ratio: 1;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  border: 0;
  background: ${(p) =>
    p.$active
      ? 'linear-gradient(110deg, #ffd28f 0%, #8d4713 25%, #ff9c2f 55%, #7c3c0c 82%, #ffc068 100%)'
      : 'linear-gradient(110deg, rgba(126, 132, 138, 0.45) 0%, rgba(44, 49, 56, 0.92) 22%, rgba(150, 155, 160, 0.35) 50%, rgba(44, 49, 56, 0.92) 80%, rgba(126, 132, 138, 0.4) 100%)'};
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - 6px) 0,
    100% 6px,
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    6px 100%,
    0 calc(100% - 6px),
    0 var(--cut)
  );
  color: var(--ink);
  padding: 0.85rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  transition: transform 0.25s var(--ease-out), filter 0.25s var(--ease-out);
  filter: ${(p) =>
    p.$active
      ? 'drop-shadow(0 14px 28px rgba(224, 141, 60, 0.4)) drop-shadow(0 0 12px rgba(242, 176, 94, 0.22))'
      : 'none'};

  /* inner surface */
  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -2;
    background: ${(p) =>
      p.$active
        ? 'radial-gradient(130% 110% at 30% 0%, rgba(255, 215, 150, 0.3), transparent 45%), linear-gradient(165deg, rgba(70, 38, 12, 0.96), rgba(20, 14, 9, 0.98))'
        : 'radial-gradient(130% 130% at 80% -20%, rgba(224, 141, 60, 0.07), #ffffff 55%, #ffffff 100%)'};
    clip-path: inherit;
  }

  /* sweeping shine */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(115deg, transparent 30%, rgba(255, 225, 175, 0.16) 48%, transparent 62%);
    transform: translateX(-110%);
    transition: transform 0.55s var(--ease-out);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    filter: drop-shadow(0 14px 26px rgba(224, 141, 60, 0.28));

    &::after {
      transform: translateX(110%);
    }
  }

  .num {
    font-family: var(--font-mono);
    font-size: 0.64rem;
    letter-spacing: 0.1em;
    color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'var(--ink-faint)')};
  }

  .symbol {
    font-family: var(--font-display);
    font-size: clamp(1.2rem, 2.6vw, 1.8rem);
    font-weight: 800;
    line-height: 1;
    color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'rgba(34, 31, 26, 0.55)')};
  }

  /* full track name — the part people actually read */
  .name {
    font-family: var(--font-body);
    font-size: clamp(0.78rem, 1.15vw, 0.92rem);
    font-weight: 600;
    line-height: 1.35;
    color: ${(p) => (p.$active ? '#fff3e2' : 'var(--ink)')};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const SpecPanel = styled.aside`
  --cut: 22px;
  position: sticky;
  top: 100px;
  isolation: isolate;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.5) 0%,
    rgba(44, 49, 56, 0.95) 18%,
    rgba(224, 141, 60, 0.85) 50%,
    rgba(42, 47, 54, 0.95) 82%,
    rgba(126, 132, 138, 0.5) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - var(--cut)) 0,
    100% var(--cut),
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    var(--cut) 100%,
    0 calc(100% - var(--cut)),
    0 var(--cut)
  );
  filter: drop-shadow(0 20px 36px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 18px rgba(224, 141, 60, 0.12));
  padding: clamp(1.8rem, 3.5vw, 2.5rem);
  min-height: 380px;

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    z-index: -1;
    background: radial-gradient(130% 130% at 100% 0%, rgba(224, 141, 60, 0.12), #ffffff 50%, #ffffff 100%);
    clip-path: inherit;
  }

  .readout {
    font-family: var(--font-mono);
    font-size: 0.66rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--copper);
    margin-bottom: 1.3rem;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dashed var(--line-strong);
    padding-bottom: 1rem;
  }

  .symbol-lg {
    font-family: var(--font-display);
    font-size: 3.6rem;
    font-weight: 800;
    line-height: 1;
    background: linear-gradient(115deg, var(--copper-bright), var(--copper-deep));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.8rem;
  }

  h2 {
    font-size: clamp(1.6rem, 2.6vw, 2rem);
    font-weight: 800;
    margin-bottom: 0.8rem;
  }

  > p {
    color: var(--ink-dim);
    font-size: 0.96rem;
    line-height: 1.7;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 980px) {
    position: static;
    min-height: 0;
  }
`;

const KeywordChips = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;

  li {
    ${hudCompactControl}
    --compact-cut: 6px;
    font-family: var(--font-mono);
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink);
    padding: 0.45rem 0.95rem;
  }
`;

export default function Topics() {
  const [active, setActive] = useState(0);
  const topic = TOPICS[active];

  return (
    <>
      <PageHero
        kicker="Conference Topics"
        index="02"
        title={
          <>
            The periodic table of <em style={{ fontStyle: 'normal', color: 'var(--copper-bright)' }}>tracks.</em>
          </>
        }
        lead="Twelve research tracks across Electronics, Materials Engineering & Nano-Technology. Select an element to inspect it."
        art={TopicsArt}
      />

      <Marquee items={TOPICS.map((t) => t.title)} duration={60} />

      <Section>
        <Container>
          <Reveal>
            <Kicker index={`${String(active + 1).padStart(2, '0')} of 12`}>Track Index</Kicker>
          </Reveal>
          <Layout>
            <Reveal>
              <TableGrid role="tablist" aria-label="Conference tracks">
                {TOPICS.map((t, i) => (
                  <ElementTile
                    key={t.title}
                    $active={active === i}
                    onClick={() => setActive(i)}
                    role="tab"
                    aria-selected={active === i}
                    aria-controls="track-spec"
                  >
                    <span className="num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="symbol">{ELEMENT_SYMBOLS[i]}</span>
                    <span className="name">{t.title}</span>
                  </ElementTile>
                ))}
              </TableGrid>
            </Reveal>

            <Reveal delay={120}>
              <SpecPanel id="track-spec" role="tabpanel" aria-live="polite">
                <div className="readout">
                  <span>Track /{String(active + 1).padStart(2, '0')}</span>
                  <span>IEMENTech '27</span>
                </div>
                <div className="symbol-lg">{ELEMENT_SYMBOLS[active]}</div>
                <h2>{topic.title}</h2>
                <p>{topic.description}</p>
                <KeywordChips>
                  {topic.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </KeywordChips>
              </SpecPanel>
            </Reveal>
          </Layout>
        </Container>
      </Section>
    </>
  );
}
